import { useState, useEffect, useRef } from 'react';

interface UseEngineSimulationProps {
  rpm: number;
  openingTime: number; // in ms
  isRunning: boolean;
  isFlameViewActive: boolean;
  simulationSpeed: number;
}

export const useEngineSimulation = ({
  rpm,
  openingTime,
  isRunning,
  isFlameViewActive,
  simulationSpeed,
}: UseEngineSimulationProps) => {
  const [enginePhase, setEnginePhase] = useState<number>(0);
  const animationFrameRef = useRef<number | null>(null);

  // Sposta la fase di 180 gradi per l'intera logica del motore.
  // Ora il TDC (iniezione/scoppio) è a 180 gradi sul quadrante.
  const effectivePhase = (enginePhase + 180) % 360;

  // Calcolo dinamico della durata dell'iniezione in gradi
  // Gradi = (RPM / 60) * 360 * (openingTime / 1000) = RPM * 0.006 * openingTime
  const injectionDurationDegrees = rpm * 0.006 * openingTime;
  const injectionStartAngle = 358;
  const injectionEndAngle = (injectionStartAngle + injectionDurationDegrees) % 360;

  let isInjecting = false;
  if (injectionStartAngle < injectionEndAngle) {
    isInjecting = effectivePhase >= injectionStartAngle && effectivePhase <= injectionEndAngle;
  } else {
    // Caso in cui scavalca lo 0 (es. inizia a 358 e finisce a 20)
    isInjecting = effectivePhase >= injectionStartAngle || effectivePhase <= injectionEndAngle;
  }

  // La combustione avviene durante l'iniezione, idealmente nella fase di espansione (0-90)
  const isCombusting = isInjecting && (effectivePhase < 90 || effectivePhase >= 358);

  const intakeOpen = effectivePhase >= 180 && effectivePhase < 270;
  const exhaustOpen = effectivePhase >= 90 && effectivePhase < 180;

  useEffect(() => {
    if (!isRunning || isFlameViewActive) {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      return;
    }

    let lastTime = performance.now();
    const animate = (time: number) => {
      const deltaTime = time - lastTime;
      lastTime = time;
      
      const degreesPerSecond = (rpm / 60) * 360;
      const phaseIncrement = (degreesPerSecond * simulationSpeed * deltaTime) / 1000;

      setEnginePhase(prev => (prev + phaseIncrement) % 360);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isRunning, isFlameViewActive, rpm, simulationSpeed]);

  return {
    enginePhase,
    setEnginePhase,
    effectivePhase,
    isInjecting,
    isCombusting,
    intakeOpen,
    exhaustOpen,
  };
};
